import React, { Component, ReactNode } from 'react'
import { useErrorBoundary } from './ErrorBoundaryContext'
import { Button, Card, Stack, Typography } from '@prometheus/components'

interface ErrorBoundaryProps {
  name: string
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

// Higher-order component to inject the error boundary context
const withErrorBoundaryContext = (WrappedComponent: React.ComponentType<any>) => {
  return (props: any) => {
    const errorBoundaryContext = useErrorBoundary()
    return <WrappedComponent {...props} errorBoundaryContext={errorBoundaryContext} />
  }
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { errorBoundaryContext: ReturnType<typeof useErrorBoundary> },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { errorBoundaryContext: ReturnType<typeof useErrorBoundary> }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to the global context
    this.props.errorBoundaryContext.addError(error, errorInfo, this.props.name)
    
    // Store error info in state for potential local fallback
    this.setState({
      error,
      errorInfo
    })

    // Log to console for development
    console.error(`Error caught by ErrorBoundary "${this.props.name}":`, error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <Card borderColor='danger' center padding="sm">
          <Stack space="none" style={{ alignItems: 'center'}}>
            <Typography retro variant="h4" color="danger">{':('} OUCH</Typography>
            <Typography retro variant="h4">=======</Typography>
            <Typography align="center">Something went wrong in <Typography component="span" color="danger">{this.props.name}.tsx</Typography></Typography>
            {this.state.error && (<Typography align="center" style={{ fontFamily: 'var(--prometheus-font-family-mono)', whiteSpace: 'pre-wrap' }} color="warning">Error: {this.state.error.message}</Typography>)}
            <div style={{ height: 'var(--prometheus-spacing-base)' }}></div>
            <Button fullWidth onClick={() => window.location.reload()} outline>Reload</Button>
          </Stack>
        </Card>
      )
    }

    return this.props.children
  }
}

// Export the wrapped component
export const ErrorBoundary = withErrorBoundaryContext(ErrorBoundaryClass)

// Export a hook for manual error reporting
export const useReportError = () => {
  const { addError } = useErrorBoundary()
  
  return (error: Error, componentName: string, additionalInfo?: any) => {
    const errorInfo: React.ErrorInfo = {
      componentStack: additionalInfo?.componentStack || 'Manual error report'
    }
    addError(error, errorInfo, componentName)
  }
}